import classnames from 'classnames'
import { lifeInsuranceOfferSelected } from 'common/actions'
import { formatCurrency, formatCurrencyWithDecimals, formatImageUrl, getContrastingColor, pluralize } from 'embed/utils'
import {
  HAVEN_SIMPLE_UUID_DEV,
  HAVEN_SIMPLE_UUID_PROD,
  HAVEN_TERM_UUID_DEV,
  HAVEN_TERM_UUID_PROD,
  LifeInsuranceCoverageTier,
  MultiProductDispatch,
  PublicClassNames,
  THE_STANDARD_UUID_DEV,
  THE_STANDARD_UUID_PROD,
} from 'embed/constants'
import { LifeInsuranceOffer, LifeInsuranceProductSubType } from 'embed/constants/models'
import { css } from 'emotion'
import * as React from 'react'
import { connect } from 'react-redux'
import { Accordion } from './Accordion'

import ThemeConsumer from 'embed/components/ThemeConsumer'
import { MultiProductState } from 'embed/reducers'
import { lifeInsuranceRatesLeadUuidSelector, lifeInsuranceRatesUuidSelector } from 'embed/selectors/rates'
import { OfferFeatureBadges } from '../../common/OfferFeatureBadges'
import { Flag } from './Flag'
import TrackingPixel from 'ui/multi-product/components/TrackingPixel'

const styles = require('./styles')
const commonStyles = require('embed/styles/results')

interface DispatchProps {
  onOfferClick: () => void
}

interface LifeInsuranceListItemProps {
  offer: LifeInsuranceOffer
  rateTableUuid?: string
  leadUuid?: string
}

interface PartnerDetails {
  key: string
  value: string
}

type Props = DispatchProps & LifeInsuranceListItemProps

function packageTypeIsLegacyLabel(labelString?: string): boolean {
  let result = false

  switch (labelString?.toLowerCase()) {
    case LifeInsuranceCoverageTier.Basic:
    case LifeInsuranceCoverageTier.Essential:
    case LifeInsuranceCoverageTier.Elite:
      result = true
      break
    default:
      result = false
  }

  return result
}

const testIdOnline = 'embed.li.online'
const testIdOther = 'embed.li.other'

const ListItemView: React.SFC<Props> = ({ offer, onOfferClick, rateTableUuid, leadUuid }) => {
  const isADB = offer.productSubType === LifeInsuranceProductSubType.AccidentalDeathBenefits
  const isWL = offer.productSubType === LifeInsuranceProductSubType.WholeLife
  const isRecommended = offer.details.recommended
  const packageType = offer.details.packageType
  const isLegacyLabel = packageTypeIsLegacyLabel(packageType)
  const shouldShowHeader = !isRecommended && !isLegacyLabel && packageType && !isADB

  return (
    <OfferContainer offer={offer}>
      {rateTableUuid && leadUuid && (
        <TrackingPixel rateTableUuid={rateTableUuid} leadUuid={leadUuid} offerUuid={offer.uuid} />
      )}
      <div
        className={classnames({
          [styles.packageTypeWithoutRecommendedContainer]: shouldShowHeader,
        })}
      >
        {offer.details.recommended ? (
          <Flag partnerUuid={offer.partner.uuid}>
            {packageType && !isLegacyLabel && <PackageType packageType={packageType} />}
          </Flag>
        ) : (
          packageType &&
          !isLegacyLabel &&
          !isADB && <PackageType className={styles.packageTypeWithoutRecommended} packageType={packageType} />
        )}
      </div>
      <div className={styles.contentWrapper}>
        <Columns>
          <Image offer={offer} onOfferClick={onOfferClick} />
          <Terms className={classnames(styles.breakpointLarge)} isADB={isADB} isWL={isWL} offer={offer} />
          <Premium offer={offer} className={styles.breakpointLarge} />
          <Cta className={styles.breakpointLarge} onClick={onOfferClick} />
        </Columns>
        <div
          className={classnames(
            styles.breakpointMedium,
            styles.breakpointSmall,
            css`
              display: flex;
              justify-content: space-between;
            `,
          )}
        >
          <Terms
            offer={offer}
            isADB={isADB}
            isWL={isWL}
            className={classnames(styles.breakpointMedium, styles.breakpointSmall)}
          />
          <Premium offer={offer} className={classnames(styles.breakpointMedium, styles.breakpointSmall)} />
        </div>
        <div>
          <ExpandableFooter
            offer={offer}
            className={styles.expandableFooter}
            features={
              <div
                className={classnames(
                  css`
                    width: 100%;
                  `,
                )}
              >
                <OfferFeatureBadges offer={offer} />
              </div>
            }
            cta={
              <Cta
                className={classnames(
                  styles.breakpointMedium,
                  styles.breakpointSmall,
                  css`
                    max-width: 165px;
                    button {
                      margin: 0;
                    }
                    margin: 0;
                  `,
                )}
                onClick={onOfferClick}
              />
            }
            isADB={isADB}
            isWL={isWL}
          />
        </div>
      </div>
    </OfferContainer>
  )
}

export const OfferContainer = ({
  children,
  className,
  offer,
}: {
  children: React.ReactNode
  className?: string
  offer: LifeInsuranceOffer
}) => (
  <ThemeConsumer>
    {({ borderRadiusSmall, colorBorder }) => (
      <li
        className={classnames(
          PublicClassNames.Offer,
          commonStyles.offer,
          styles.offer,
          { [styles.recommended]: offer.details.recommended },
          css`
            border-color: ${colorBorder};
            border-radius: ${borderRadiusSmall};
          `,
          className,
        )}
        data-testid={offer.details.onlineAppAvailable ? testIdOnline : testIdOther}
      >
        {children}
      </li>
    )}
  </ThemeConsumer>
)

export const Columns = ({ children, className }: { children: React.ReactNode; className?: string }) => (
  <div className={classnames(styles.columns, className)}>{children}</div>
)

export const Image = ({ offer, onOfferClick }: { offer: LifeInsuranceOffer; onOfferClick: () => void }) => (
  <img
    alt={offer.partner.name}
    className={styles.image}
    onClick={onOfferClick}
    src={formatImageUrl(offer.partner.imageUrl)}
  />
)

export const Premium = ({ offer, className }: { offer: LifeInsuranceOffer; className?: string }) => (
  <div className={classnames(styles.premium, className)}>
    <span className={styles.amount}>${formatCurrencyWithDecimals(offer.details.monthlyPremiumAmount)}</span>{' '}
    <Label fontSize="14px">per month</Label>
  </div>
)

export const Terms = ({
  className,
  isADB = false,
  isWL = false,
  offer,
}: {
  className?: string
  isADB: boolean
  isWL: boolean
  offer: LifeInsuranceOffer
}) => (
  <ul className={classnames(styles.terms, className)}>
    <li className={styles.termsItem}>
      <Label>{isADB ? 'Accidental Coverage to Age 80' : isWL ? 'Lifetime Coverage' : 'Coverage'}</Label>
      <span className={styles.value}>{`$${formatCurrency(offer.details.policyAmount)}`}</span>
    </li>
    {offer.details.termLength && offer.details.termUnit && (
      <li>
        <Label>Term</Label>
        <span className={styles.value}>
          {offer.details.termLength} {pluralize(offer.details.termUnit, offer.details.termLength)}
        </span>
      </li>
    )}
  </ul>
)

const Cta = ({ className, onClick }) => (
  <ThemeConsumer>
    {({ ctaStyles, colorTextPrimary, borderRadiusSmall }) => (
      <div className={classnames(styles.buttonContainer, className)}>
        <button
          className={classnames(
            commonStyles.button,
            styles.button,
            css(`
                color: ${getContrastingColor(ctaStyles?.backgroundColor, { black: colorTextPrimary })};
                border-radius: ${borderRadiusSmall};
                &:hover {opacity: 0.84;}
              `),
          )}
          style={ctaStyles}
          onClick={onClick}
        >
          Continue
        </button>
      </div>
    )}
  </ThemeConsumer>
)

export const Footer = ({ className, offer }: { className?: string; offer: LifeInsuranceOffer }) => (
  <ThemeConsumer>
    {({ colorBorder }) => (
      <div
        className={classnames(
          styles.footer,
          css`
            border-color: ${colorBorder};
            &:before {
              border-bottom-color: ${colorBorder};
            }
            text-align: left;
          `,
          className,
        )}
      >
        <span className={styles.footerLabel}>Founded</span>
        <span className={styles.footerValue}>{offer.partner.foundedYear}</span>
        <span className={styles.footerLabel}>A.M. Best Rating</span>
        <span className={styles.footerValue}>{offer.partner.amBestRating}</span>
      </div>
    )}
  </ThemeConsumer>
)

export const ExpandableFooter = ({
  offer,
  features,
  cta,
  isADB = false,
  isWL = false,
}: {
  className?: string
  offer: LifeInsuranceOffer
  features?: JSX.Element
  cta?: JSX.Element
  isADB: boolean
  isWL: boolean
}) => {
  const details = [
    { key: 'A.M. Best Rating', value: offer.partner.amBestRating },
    { key: 'Founded', value: offer.partner.foundedYear },
    { key: 'Headquarters', value: offer.partner.headquarters },
    { key: 'Assets', value: offer.partner.assets },
    ...(isADB || isWL
      ? []
      : [
          { key: 'Convert to Permanent Policy', value: offer.details.convertToPermanent ? 'Included' : 'Not Included' },
        ]),
    { key: 'Child Rider', value: offer.details.childRider },
    {
      key: 'Online Application',
      value:
        offer.details.onlineAppAvailable !== undefined
          ? offer.details.onlineAppAvailable
            ? 'Yes'
            : 'No'
          : offer.details.onlineAppAvailable,
    },
    { key: 'Policy Delivery Method', value: offer.details.deliveryMethod?.toUpperCase() },
    { key: 'Exam Required', value: offer.details.examRequired },
    ...([
      HAVEN_SIMPLE_UUID_DEV,
      HAVEN_SIMPLE_UUID_PROD,
      HAVEN_TERM_UUID_DEV,
      HAVEN_TERM_UUID_PROD,
      THE_STANDARD_UUID_DEV,
      THE_STANDARD_UUID_PROD,
    ].includes(offer.partner.uuid)
      ? [{ key: 'Description', value: offer.partner.description }]
      : []),
    ...([HAVEN_SIMPLE_UUID_DEV, HAVEN_SIMPLE_UUID_PROD, HAVEN_TERM_UUID_DEV, HAVEN_TERM_UUID_PROD].includes(
      offer.partner.uuid,
    )
      ? [{ key: 'Disclosure', value: offer.partner.disclaimer }]
      : []),
  ]

  const filteredDetails = details.filter((d: PartnerDetails) => d.value)

  const children = filteredDetails.reduce((detailsArray: JSX.Element[], detail: PartnerDetails) => {
    const elem = (
      <div key={detail.key} className={styles.detailsContainer}>
        <p className={styles.detailsLabel}>{detail.key}</p>
        <p className={styles.detailsValue}>{detail.value}</p>
      </div>
    )
    return [...detailsArray, elem]
  }, [])

  return (
    <Accordion
      children={children}
      collapsed={true}
      minItemsShown={0}
      maxHeight={0}
      paddingRight={0}
      toggleFullWidth={true}
      accordionPaddingLeft={false}
      showMask={false}
      features={features}
      cta={cta}
    />
  )
}

const PackageType = ({ className, packageType }: { className?: string; packageType: string }) => (
  <ThemeConsumer>
    {({ colorSecondary }) => (
      <span
        className={classnames(
          className,
          styles.packageType,
          css`
            color: ${colorSecondary};
          `,
        )}
      >
        {packageType}
      </span>
    )}
  </ThemeConsumer>
)

export const Label = ({ children, fontSize = '16px' }) => (
  <ThemeConsumer>
    {({ colorTextSecondary }) => (
      <span
        className={classnames(
          styles.label,
          css`
            color: ${colorTextSecondary};
            font-size: ${fontSize};
          `,
        )}
      >
        {children}
      </span>
    )}
  </ThemeConsumer>
)

const mapStateToProps = (state: MultiProductState) => ({
  leadUuid: lifeInsuranceRatesLeadUuidSelector(state),
  rateTableUuid: lifeInsuranceRatesUuidSelector(state),
})

const mapDispatchToProps = (dispatch: MultiProductDispatch, ownProps: LifeInsuranceListItemProps) => ({
  onOfferClick: () => dispatch(lifeInsuranceOfferSelected(ownProps.offer)),
})

export const ListItem = connect(mapStateToProps, mapDispatchToProps)(ListItemView)
